import * as React from "react";
import { graphql } from "gatsby";
import styled from "styled-components";
import { GatsbyImage } from "gatsby-plugin-image";
import PageContainer from "../pageContainer";
import { format } from "date-fns";
import { SectionTitle } from "../typography";
import theme, { when } from "../../styles/theme";
import pageBackground from "../../images/page_background_red.jpg";

export const fragment = graphql`
  fragment NewsContent on ContentfulPageSectionNews {
    name
    title
    news {
      title
      link
      previewImage {
        gatsbyImageData(layout: CONSTRAINED, width: 552, height: 552)
        description
      }
      date
      brand {
        name
        smallLogo {
          gatsbyImageData(layout: CONSTRAINED, width: 52, height: 52)
          description
        }
      }
    }
    internal {
      type
    }
  }
`;

const Wrapper = styled.section`
  background: ${(props) => props.theme.colors.red};
  color: ${(props) => props.theme.colors.white};
  position: relative;
`;

const ContainerBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  //z-index: -1;
  background-image: url(${pageBackground});
  background-repeat: repeat;
  background-position: center;
`;

const NewsSection = styled(PageContainer)`
  //margin: 15rem 0;
  padding-top: 10rem;
  padding-bottom: 10rem;
  position: relative;
  z-index: 1;
`;

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;

  ${when("sm")} {
    margin: 0 -${(props) => props.theme.gutterWidth.sm / 2}rem;
  }

  ${when("md")} {
    margin-left: -${(props) => props.theme.gutterWidth.md / 2}rem;
    margin-right: -${(props) => props.theme.gutterWidth.md / 2}rem;
  }

  ${when("lg")} {
    margin-left: -${(props) => props.theme.gutterWidth.lg / 2}rem;
    margin-right: -${(props) => props.theme.gutterWidth.lg / 2}rem;
  }

  ${when("xl")} {
    margin-left: -${(props) => props.theme.gutterWidth.xl / 2}rem;
    margin-right: -${(props) => props.theme.gutterWidth.xl / 2}rem;
  }

  ${when("xxl")} {
    margin-left: -${(props) => props.theme.gutterWidth.xxl / 2}rem;
    margin-right: -${(props) => props.theme.gutterWidth.xxl / 2}rem;
  }
`;

const NewsContent = styled.div`
  margin-top: 3rem;
  padding: ${(props) => props.theme.gutterWidth.xs / 2}rem;

  ${when("sm")} {
    width: 50%;
    padding: ${(props) => props.theme.gutterWidth.sm / 2}rem;
  }

  ${when("md")} {
    padding: ${(props) => props.theme.gutterWidth.md / 2}rem;
  }

  ${when("lg")} {
    width: 33.33%;
    padding: ${(props) => props.theme.gutterWidth.lg / 2}rem;
  }

  ${when("xl")} {
    padding: ${(props) => props.theme.gutterWidth.xl / 2}rem;
  }

  ${when("xxl")} {
    padding: ${(props) => props.theme.gutterWidth.xxl / 2}rem;
  }
`;

const Title = styled(SectionTitle)`
  text-align: center;
`;
const Brand = styled.div`
  display: flex;
  align-items: center;
`;

const LogoImage = styled(GatsbyImage)`
  width: 5.2rem;
  height: 5.2rem;
  border-radius: 50%;
  transform: translateZ(0);
  overflow: hidden;
  display: block;
`;

const BrandName = styled.div`
  margin-left: 1.2rem;
`;

const Content = styled.a`
  display: block;
`;

const NewsImage = styled.div`
  margin-top: 1rem;
`;

const NewsTitle = styled.h3`
  font-size: 2rem;
  //color: ${(props) => props.theme.colors.darkBlue};
  margin-top: 2rem;
  margin-bottom: 0;
`;

const StyledDate = styled.div`
  font-size: 1.4rem;
`;

const News = ({ data }) => {
  return (
    <Wrapper>
      <ContainerBackground />
      <NewsSection>
        <Title color={theme.colors.white}>{data.title}</Title>
        <Container>
          {data.news.map((news, index) => {
            const { brand, title, previewImage, date, link } = news;
            return (
              <NewsContent key={index}>
                <Brand>
                  <LogoImage
                    alt={brand.smallLogo?.description}
                    image={brand.smallLogo?.gatsbyImageData}
                  />
                  <BrandName>{brand.name}</BrandName>
                </Brand>
                <Content href={link} target="_blank">
                  <NewsImage>
                    <GatsbyImage
                      alt={previewImage?.description}
                      image={previewImage?.gatsbyImageData}
                    />
                  </NewsImage>
                  <NewsTitle>{title}</NewsTitle>
                  <StyledDate>
                    {format(new Date(date), "dd. MM. yyyy")}
                  </StyledDate>
                </Content>
              </NewsContent>
            );
          })}
        </Container>
      </NewsSection>
    </Wrapper>
  );
};

export const newsName = "ContentfulPageSectionNews";

export default News;
